import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { notificar } from '../utils/notify';
import '../styles/painelOperador.css';
import '../styles/gestaoBpoRh.css';

// BPO RH — Importação Omie: a operadora sobe o Relatório de Líquidos (.xls do
// Domínio), o Saber cruza por CPF com a Convenia, aplica as regras da Omie
// (categoria, projeto por CNPJ, departamento, vencimento no 5º dia útil) e
// exporta a planilha Omie_Contas_Pagar + aba Nomes. Backend: core/views_omie_folha.py
const API = process.env.REACT_APP_API_BASE;
const URL_OMIE = `${API}/api/bporh/omie/`;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('access')}` });
const brl = (v) => (v == null ? '—' : `R$ ${Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
// LGPD: o CPF completo nunca aparece na tela, só os dígitos do meio. Valor fora
// do formato não é devolvido cru (poderia ser um CPF com pontuação ou truncado).
const mascara = (cpf) => {
  const d = String(cpf || '').replace(/\D/g, '');
  return d.length === 11 ? `***.${d.slice(3, 6)}.${d.slice(6, 9)}-**` : (d ? 'CPF inválido' : '—');
};
// competência padrão = mês anterior (a folha que acabou de fechar). Monta a
// string pela data local: toISOString() é UTC e, perto da meia-noite do dia 1,
// devolveria o mês errado; setDate(1) evita o estouro de 31/03 − 1 mês = 03/03.
const mesAtual = () => {
  const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};
// sentinela da opção "limpar": '' já é o "Escolha…", e duas opções com o mesmo
// valor deixariam o select ambíguo
const LIMPAR = '__limpar__';
// FGTS: grava como salvo o departamento que hoje vem da Convenia (escolher a mesma
// opção do select não dispara onChange, por isso a opção à parte)
const FIXAR = '__fixar__';
// o 403 de operador interno vem com a chave `erro`, não `mensagem`
const erroMsg = (e, padrao) => e.response?.data?.mensagem || e.response?.data?.erro || e.response?.data?.error || padrao;
// CNPJ de filial (dado da empresa, não pessoal): 14 dígitos → 00.000.000/0000-00
const cnpjFmt = (c) => {
  const d = String(c || '').replace(/\D/g, '');
  return d.length === 14 ? `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}` : (c || '—');
};
const ORIGEM_FGTS = { salvo: 'Salvo', convenia: 'Convenia', lote: 'Neste mês' };

// textarea "chave=valor" (um por linha) → objeto; divide só no 1º "=" porque o
// nome do projeto/departamento pode conter "="
// devolve também quantas linhas não vazias vieram sem "=", para avisar a operadora
// em vez de descartar em silêncio
const parsePares = (texto, normalizarChave = (k) => k) => {
  const obj = {};
  let ignoradas = 0;
  texto.split('\n').forEach((l) => {
    const i = l.indexOf('=');
    if (i < 0) { if (l.trim()) ignoradas += 1; return; }
    const k = normalizarChave(l.slice(0, i).trim());
    const v = l.slice(i + 1).trim();
    if (k && v) obj[k] = v;
  });
  return { obj, ignoradas };
};
const paresTexto = (obj) => Object.entries(obj || {}).map(([k, v]) => `${k}=${v}`).join('\n');

const BpoRhImportacaoOmie = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const clienteId = searchParams.get('cliente') || '';
  const mes = searchParams.get('mes') || mesAtual();
  // tipo de planilha: 'salarios' (Relatório de Líquidos) ou 'fgts' (rateio do FGTS)
  const tipo = searchParams.get('tipo') === 'fgts' ? 'fgts' : 'salarios';
  const ehFgts = tipo === 'fgts';
  const [clientes, setClientes] = useState(null);
  const [admLista, setAdmLista] = useState(false);
  const [dadosEstado, setDados] = useState(null);
  // dados de outro tipo (ainda não recarregados após a troca) não são pintados
  const dados = dadosEstado && (dadosEstado.tipo || 'salarios') === tipo ? dadosEstado : null;
  const [soPendentes, setSoPendentes] = useState(false); // FGTS: filtro da tabela de colaboradores
  const [filtroDep, setFiltroDep] = useState('');         // FGTS: clicar no rateio filtra quem está lá
  const colabFgtsRef = useRef(null);
  // trocar de empresa/mês/tipo zera o filtro (o departamento pode nem existir no outro lote)
  useEffect(() => { setFiltroDep(''); }, [clienteId, mes, tipo]);
  const [progFgts, setProgFgts] = useState('');          // FGTS: "2/3" durante o envio em lote
  const [carregando, setCarregando] = useState(false);
  const [ocupado, setOcupado] = useState(''); // qual ação está em andamento
  // coluna "Fornecedor" da Omie na hora de exportar: 'cpf' ou 'nome'. Começa no padrão
  // configurado da empresa (adm) e a operadora pode trocar antes de baixar.
  const [fornecedor, setFornecedor] = useState('');
  const [cfg, setCfg] = useState(null);       // modal de configuração (só adm)
  const [semCpf, setSemCpf] = useState(null); // nomes do modelo que vieram sem CPF
  const relatorioRef = useRef(null);
  const modeloRef = useRef(null);
  const fgtsRef = useRef(null);
  // contra corrida: se a operadora troca empresa/mês/tipo com uma requisição no ar,
  // a resposta antiga não pode sobrescrever a tela nova
  const reqRef = useRef(0);
  const atualRef = useRef({ clienteId, mes, tipo });
  atualRef.current = { clienteId, mes, tipo };
  const aindaAtual = (c, m, t) => atualRef.current.clienteId === c && atualRef.current.mes === m && atualRef.current.tipo === t;

  // 'salarios' é o padrão: não vai para a URL
  const trocar = (patch) => {
    const p = { cliente: clienteId, mes, tipo: ehFgts ? 'fgts' : '', ...patch };
    if (p.tipo === 'salarios') p.tipo = '';
    setSearchParams(Object.fromEntries(Object.entries(p).filter(([, v]) => v)));
  };

  // lista de empresas: com uma só, já abre direto (operadora com um cliente)
  useEffect(() => {
    axios.get(URL_OMIE, { params: { _: Date.now() }, headers: headers() })
      .then((r) => {
        const cls = r.data.clientes || [];
        setClientes(cls); setAdmLista(!!r.data.eh_adm);
        if (cls.length === 1 && !searchParams.get('cliente')) {
          const t = searchParams.get('tipo');
          setSearchParams({ cliente: String(cls[0].id), mes: searchParams.get('mes') || mesAtual(), ...(t === 'fgts' ? { tipo: t } : {}) });
        }
      })
      .catch((e) => notificar(`❌ ${erroMsg(e, 'Não foi possível carregar as empresas')}`));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const carregar = useCallback(() => {
    const id = ++reqRef.current;
    // sem empresa: limpa também o spinner de uma requisição anterior que ficou órfã
    if (!clienteId) { setDados(null); setCarregando(false); return; }
    setCarregando(true);
    axios.get(URL_OMIE, { params: { cliente_id: clienteId, mes, tipo, _: Date.now() }, headers: headers() })
      // o tipo vai junto no estado: se o backend não devolver, vale o pedido
      .then((r) => { if (id === reqRef.current) setDados({ ...r.data, tipo: r.data.tipo || tipo }); })
      .catch((e) => { if (id !== reqRef.current) return; setDados(null); notificar(`❌ ${erroMsg(e, 'Não foi possível carregar a empresa')}`); })
      .finally(() => { if (id === reqRef.current) setCarregando(false); });
  }, [clienteId, mes, tipo]);
  useEffect(() => { setSemCpf(null); carregar(); }, [carregar]);

  const enviarArquivo = (arq, acao) => {
    if (!arq) return;
    const c0 = clienteId; const m0 = mes; const t0 = tipo;
    const fd = new FormData();
    if (acao) fd.append('acao', acao);
    fd.append('tipo', t0);
    fd.append('cliente_id', clienteId); fd.append('mes', mes); fd.append('arquivo', arq);
    setOcupado(acao || 'relatorio');
    axios.post(URL_OMIE, fd, { headers: headers() })
      .then((r) => {
        if (acao === 'modelo') {
          // o servidor recalcula as linhas do relatório já lido com o de-para novo;
          // conta quantas ficaram com departamento salvo para a operadora ver o efeito
          const lt = r.data.lote;
          const aplicados = lt ? (lt.linhas || []).filter((l) => l.departamento_origem === 'salvo').length : 0;
          notificar(`✅ ${r.data.mensagem || `${r.data.gravados} colaborador(es) gravado(s)`}${lt ? ` · ${aplicados} departamento(s) aplicados ao relatório deste mês` : ''}`);
          setSemCpf((r.data.sem_cpf || []).length ? r.data.sem_cpf : null);
        } else {
          notificar(`✅ ${r.data.mensagem}`);
        }
        if (r.data.lote && aindaAtual(c0, m0, t0)) setDados((d) => ({ ...d, lote: r.data.lote }));
        if (aindaAtual(c0, m0, t0)) carregar(); // atualiza pessoas salvas / histórico
      })
      .catch((e) => notificar(`❌ ${erroMsg(e, 'Não foi possível ler o arquivo')}`))
      .finally(() => {
        setOcupado('');
        [relatorioRef, modeloRef].forEach((ref) => { if (ref.current) ref.current.value = ''; });
      });
  };

  // FGTS: um arquivo por POST, em sequência (o CNPJ repetido substitui o anterior no
  // servidor); erro de um arquivo não interrompe os demais
  const enviarFgts = async (lista) => {
    const arqs = Array.from(lista || []);
    if (!arqs.length) return;
    const c0 = clienteId; const m0 = mes; const t0 = 'fgts';
    let lidos = 0; let mesArquivo = '';
    setOcupado('fgts');
    try {
      for (let i = 0; i < arqs.length; i += 1) {
        setProgFgts(arqs.length > 1 ? `${i + 1}/${arqs.length}` : '');
        const fd = new FormData();
        fd.append('tipo', t0); fd.append('cliente_id', c0); fd.append('mes', m0); fd.append('arquivo', arqs[i]);
        try {
          const r = await axios.post(URL_OMIE, fd, { headers: headers() });
          lidos += 1;
          // o servidor grava na competência do arquivo, que pode não ser a da tela
          if (r.data.mes && r.data.mes !== m0) { mesArquivo = r.data.mes; notificar(`ℹ️ ${r.data.mensagem}`); }
          else if (r.data.lote && aindaAtual(c0, m0, t0)) setDados((d) => ({ ...d, lote: r.data.lote }));
        } catch (e) {
          notificar(`❌ ${arqs[i].name}: ${erroMsg(e, 'Não foi possível ler o arquivo')}`);
        }
      }
    } finally {
      setOcupado(''); setProgFgts('');
      if (fgtsRef.current) fgtsRef.current.value = '';
    }
    if (lidos) notificar(`✅ ${lidos} arquivo(s) lido(s)${lidos < arqs.length ? ` · ${arqs.length - lidos} com erro` : ''}`);
    if (!aindaAtual(c0, m0, t0)) return;
    if (mesArquivo) trocar({ mes: mesArquivo }); else carregar();
  };

  const post = (corpo) => axios.post(URL_OMIE, { cliente_id: Number(clienteId), mes, tipo, ...corpo }, { headers: headers() });

  // projeto/departamento escolhido fica salvo por CPF para os próximos meses
  const salvarPessoa = (linha, campo, valor) => {
    const c0 = clienteId; const m0 = mes; const t0 = tipo;
    setOcupado(`pessoa-${linha.cpf}`);
    post({ acao: 'pessoa', cpf: linha.cpf, nome: linha.nome, [campo]: valor })
      .then((r) => { if (aindaAtual(c0, m0, t0)) setDados((d) => ({ ...d, lote: r.data.lote })); notificar(`✅ ${r.data.mensagem}`); })
      .catch((e) => notificar(`❌ ${erroMsg(e, 'Não foi possível salvar')}`))
      .finally(() => setOcupado(''));
  };

  // FGTS: departamento por pessoa (o servidor acha a pessoa pelo nome no lote do mês e,
  // com CPF da Convenia, guarda para os próximos meses); '' limpa
  const salvarPessoaFgts = (p, departamento) => {
    const c0 = clienteId; const m0 = mes; const t0 = 'fgts';
    setOcupado(`fgts-${p.nome}`);
    post({ acao: 'pessoa_fgts', tipo: t0, nome: p.nome, departamento })
      .then((r) => {
        if (r.data.lote && aindaAtual(c0, m0, t0)) setDados((d) => ({ ...d, lote: r.data.lote }));
        notificar(`✅ ${r.data.mensagem || 'Departamento salvo'}`);
      })
      .catch((e) => notificar(`❌ ${erroMsg(e, 'Não foi possível salvar')}`))
      .finally(() => setOcupado(''));
  };

  const exportar = (loteId) => {
    // empresa/mês/tipo do momento do clique: o recarregar só vale se a operadora não trocou
    const c0 = clienteId; const m0 = mes; const t0 = tipo;
    setOcupado(`exportar-${loteId}`);
    // no FGTS o fornecedor é fixo da config (RECEITA FEDERAL - FGTS): o seletor não vale
    const params = { exportar: loteId, _: Date.now() };
    if (t0 !== 'fgts') params.fornecedor = fornecedor || dados?.config?.fornecedor || 'cpf';
    axios.get(URL_OMIE, { params, headers: headers(), responseType: 'blob' })
      .then((r) => {
        const cd = r.headers['content-disposition'] || '';
        const m = cd.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
        const nome = m ? decodeURIComponent(m[1]) : `Omie_${t0 === 'fgts' ? 'FGTS' : 'Contas_Pagar'}_${m0}.xlsx`;
        const url = window.URL.createObjectURL(r.data);
        const a = document.createElement('a');
        a.href = url; a.download = nome; document.body.appendChild(a); a.click(); a.remove();
        // revogar no mesmo tick pode cancelar o download em alguns navegadores
        setTimeout(() => window.URL.revokeObjectURL(url), 0);
        if (aindaAtual(c0, m0, t0)) carregar(); // relê a tela (o lote pode ter mudado no servidor)
      })
      .catch(async (e) => {
        // com responseType blob o erro também chega como blob: precisa ler o texto
        let msg = 'Não foi possível exportar a planilha';
        try { msg = JSON.parse(await e.response.data.text()).mensagem || msg; } catch (_) { /* mantém a mensagem padrão */ }
        notificar(`❌ ${msg}`);
      })
      .finally(() => setOcupado(''));
  };

  const abrirCfg = () => {
    const c = dados?.config || {};
    setCfg({
      projetos: paresTexto(c.projetos),
      departamentos: (c.departamentos || []).join('\n'),
      depara: paresTexto(c.depara_departamento),
      conta_corrente: c.conta_corrente || '',
      categoria_clt: c.categoria_clt || '',
      categoria_estagio: c.categoria_estagio || '',
      fornecedor: c.fornecedor || 'cpf',
      fgts_fornecedor: c.fgts_fornecedor || '',
      fgts_categoria: c.fgts_categoria || '',
      fgts_conta: c.fgts_conta || '',
    });
  };
  const salvarCfg = () => {
    const c0 = clienteId; const m0 = mes; const t0 = tipo;
    // monta o objeto já pronto e manda direto (sem depender de setState assíncrono)
    const proj = parsePares(cfg.projetos, (k) => k.replace(/\D/g, '')); // CNPJ só com números
    const dep = parsePares(cfg.depara);
    const ignoradas = proj.ignoradas + dep.ignoradas;
    if (ignoradas) notificar(`⚠️ ${ignoradas} linha(s) sem '=' foram ignoradas`);
    const corpo = {
      acao: 'config',
      projetos: proj.obj,
      departamentos: cfg.departamentos.split('\n').map((s) => s.trim()).filter(Boolean),
      depara_departamento: dep.obj,
      conta_corrente: cfg.conta_corrente.trim(),
      categoria_clt: cfg.categoria_clt.trim(),
      categoria_estagio: cfg.categoria_estagio.trim(),
      fornecedor: cfg.fornecedor || 'cpf',
      fgts_fornecedor: cfg.fgts_fornecedor.trim(),
      fgts_categoria: cfg.fgts_categoria.trim(),
      fgts_conta: cfg.fgts_conta.trim(),
    };
    setOcupado('config');
    post(corpo)
      .then((r) => {
        notificar(`✅ ${r.data.mensagem}`); setCfg(null);
        // a config nova recalcula as linhas no servidor: usa o lote do tipo em tela
        // (o servidor devolve os dois) e relê o resto
        const lt = t0 === 'fgts' ? r.data.lote_fgts : r.data.lote;
        if (aindaAtual(c0, m0, t0)) { if (lt) setDados((d) => ({ ...d, lote: lt })); carregar(); }
      })
      .catch((e) => notificar(`❌ ${erroMsg(e, 'Não foi possível salvar a configuração')}`))
      .finally(() => setOcupado(''));
  };

  const ehAdm = dados ? !!dados.eh_adm : admLista;
  const lote = dados?.lote;
  const config = dados?.config || {};
  const projetos = [...new Set(Object.values(config.projetos || {}))];
  const departamentos = config.departamentos || [];
  const podeExportar = !!lote && !lote.pendentes;
  const tituloExportar = !lote ? (ehFgts ? 'Suba o Encargos da Empresa primeiro' : 'Suba o Relatório de Líquidos primeiro')
    : lote.pendentes ? `Resolva as ${lote.pendentes} pendência(s) antes de exportar` : 'Baixar a planilha para importar na Omie';
  // FGTS: filiais esperadas (config) × lidas no mês
  const cnpjs = dados?.cnpjs || [];
  const cnpjsLidos = cnpjs.filter((c) => c.lido).length;
  const nomeFilial = (cnpj) => {
    const d = String(cnpj || '').replace(/\D/g, '');
    return (config.projetos || {})[d] || cnpjFmt(cnpj);
  };
  const pessoasFgts = ((ehFgts && lote?.pessoas) || [])
    .filter((p) => !soPendentes || (p.no_rateio !== false && p.situacao === 'pendente'))
    .filter((p) => !filtroDep || p.departamento === filtroDep);
  // clicar num departamento do rateio mostra quem está nele (pedido de 14/10/2026)
  const verDepartamento = (dep) => {
    setFiltroDep((d) => (d === dep ? '' : dep));
    setTimeout(() => colabFgtsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  };
  const somaFgts = (campo) => ((ehFgts && lote?.linhas) || []).reduce((s, l) => s + Number(l[campo] || 0), 0);

  return (
    <div className="painel-op"><div className="container-fluid mt-4 shadow p-4 rounded bg-white">
      <div className="header-wrapper d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <div className="d-flex align-items-center"><i className="fas fa-file-export header-icon me-2" aria-hidden="true"></i>
          <h2 className="titulo-pagina m-0">BPO RH — Importação Omie</h2></div>
        <div className="gr-toolbar">
          <select className="form-select form-select-sm" style={{ width: 230 }} value={clienteId} onChange={(e) => trocar({ cliente: e.target.value })} aria-label="Empresa">
            <option value="">{clientes ? 'Escolha a empresa…' : 'Carregando…'}</option>
            {(clientes || []).map((c) => <option key={c.id} value={c.id}>{c.cliente}</option>)}
          </select>
          <input type="month" className="form-control form-control-sm" style={{ width: 150 }} value={mes} onChange={(e) => e.target.value && trocar({ mes: e.target.value })} aria-label="Competência" />
          <select className="form-select form-select-sm" style={{ width: 160 }} value={tipo} onChange={(e) => trocar({ tipo: e.target.value })} aria-label="Tipo de planilha" title="Tipo de planilha">
            <option value="salarios">Salários</option>
            <option value="fgts">Rateio FGTS</option>
          </select>
          <button type="button" className="gr-btn" onClick={carregar} disabled={carregando || !clienteId}><i className={`fas ${carregando ? 'fa-spinner fa-spin' : 'fa-rotate'}`} aria-hidden="true"></i>Atualizar</button>
          {ehAdm && clienteId && <button type="button" className="gr-btn" onClick={abrirCfg} disabled={!dados}><i className="fas fa-gear" aria-hidden="true"></i>Configurar</button>}
        </div>
      </div>

      {!clienteId && clientes && (
        <div className="text-muted small py-4 text-center">{clientes.length ? 'Escolha a empresa para começar.' : 'Nenhuma empresa configurada para a Omie.'}</div>)}

      {dados && (<>
        {ehFgts ? (<>
        {/* ── Rateio do FGTS: Encargos da Empresa (Domínio) → uma linha por departamento ── */}
        <div className="alert alert-light border small py-2"><i className="fas fa-circle-info me-2 text-primary" aria-hidden="true"></i>
          Uma linha por departamento, fornecedor <b>{config.fgts_fornecedor || '—'}</b>, vencimento <b>{dados.vencimento_fgts || '—'}</b> (dia 20 do mês seguinte, antecipando para dia útil).
          Pode subir o arquivo com todas as filiais ou um por filial — o CNPJ repetido substitui o anterior.</div>

        <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
          <input ref={fgtsRef} type="file" hidden multiple accept=".xls" onChange={(e) => enviarFgts(e.target.files)} />
          <button type="button" className="gr-btn primaria" disabled={!!ocupado} onClick={() => fgtsRef.current?.click()}>
            <i className={`fas ${ocupado === 'fgts' ? 'fa-spinner fa-spin' : 'fa-file-arrow-up'}`} aria-hidden="true"></i>
            {ocupado === 'fgts' ? `Lendo os encargos${progFgts ? ` ${progFgts}` : ''}…` : 'Subir Encargos da Empresa (.xls)'}</button>
          {dados.pessoas_salvas != null && <span className="po-sub">{dados.pessoas_salvas} colaborador(es) com departamento salvo</span>}
          <button type="button" className="gr-btn primaria ms-auto" disabled={!podeExportar || !!ocupado} title={tituloExportar} onClick={() => exportar(lote.id)}>
            <i className={`fas ${lote && ocupado === `exportar-${lote.id}` ? 'fa-spinner fa-spin' : 'fa-file-excel'}`} aria-hidden="true"></i>Exportar planilha</button>
        </div>

        {lote && !departamentos.length && (
          <div className="alert alert-warning small py-2"><i className="fas fa-triangle-exclamation me-2" aria-hidden="true"></i>
            Ainda não há lista de departamentos da Omie — peça ao administrador para configurar os departamentos.</div>)}

        {lote ? (<>
          <div className="row g-3 mb-3">
            {[
              ['Pessoas no rateio', lote.qtd, 'fa-users', undefined],
              ['Total', brl(lote.total), 'fa-sack-dollar', undefined],
              ['e-Consignado', brl(somaFgts('consignado')), 'fa-building-columns', undefined],
              ['Pendências', lote.pendentes, 'fa-triangle-exclamation', lote.pendentes > 0 ? '#a11d2b' : undefined],
              ['CNPJs lidos', `${cnpjsLidos}/${cnpjs.length}`, 'fa-building', cnpjs.length && cnpjsLidos < cnpjs.length ? '#a11d2b' : undefined],
            ].map(([rot, val, ic, cor]) => (
              <div className="col-6 col-lg" key={rot}>
                <section className="po-card po-enter h-100"><div className="po-card-body">
                  <div className="po-sub"><i className={`fas ${ic} me-1`} aria-hidden="true"></i>{rot}</div>
                  <div className="fs-4 fw-bold tabular" style={{ color: cor }}>{val}</div>
                  {rot === 'e-Consignado' && (
                    <div className="po-sub small">{lote.consignado?.qtd ? `${lote.consignado.qtd} parcela(s) da Folha de ${dados.mes}` : `Nada importado na Folha de ${dados.mes}`}</div>)}
                  {rot === 'CNPJs lidos' && cnpjs.length > 0 && (
                    <div className="small mt-1">{cnpjs.map((c) => {
                      const info = (lote.cnpjs || {})[String(c.cnpj || '').replace(/\D/g, '')] || (lote.cnpjs || {})[c.cnpj];
                      return (
                        <div key={c.cnpj} title={info?.arquivo ? `Arquivo: ${info.arquivo}` : 'Ainda não lido neste mês'}>
                          <i className={`fas ${c.lido ? 'fa-check' : 'fa-xmark'} me-1`} style={{ color: c.lido ? '#146c43' : '#a11d2b' }} aria-hidden="true"></i>
                          {c.projeto || cnpjFmt(c.cnpj)}<span className="visually-hidden">{c.lido ? ' (lido)' : ' (não lido)'}</span></div>);
                    })}</div>)}
                </div></section></div>))}
          </div>

          {(lote.avisos || []).length > 0 && (
            <div className="alert alert-warning small py-2">{lote.avisos.map((a, i) => <div key={`${i}-${a}`}><i className="fas fa-triangle-exclamation me-1" aria-hidden="true"></i>{a}</div>)}</div>)}

          <section className="po-card po-enter mb-3"><header className="po-card-head">
            <h3><i className="fas fa-sitemap" aria-hidden="true"></i>Rateio por departamento · {dados.mes}</h3>
            <span className="po-sub">{lote.arquivo ? `${lote.arquivo} · ` : ''}{lote.gerado_por} · {lote.gerado_em}</span></header>
            <div className="po-card-body"><div className="po-scroll"><table className="table table-hover po-table align-middle mb-0" style={{ fontSize: '.86rem' }}>
              <thead><tr><th style={{ minWidth: 200 }}>Departamento</th><th className="text-end">Pessoas</th><th className="text-end">FGTS</th><th className="text-end">Rescisório</th><th className="text-end">Consignado</th><th className="text-end">Valor</th><th>Vencimento</th></tr></thead>
              <tbody>{(lote.linhas || []).map((l, i) => (
                <tr key={`${l.departamento}-${i}`} onClick={() => l.departamento && verDepartamento(l.departamento)}
                  title={l.departamento ? `Ver quem está em ${l.departamento}` : undefined}
                  style={{ cursor: l.departamento ? 'pointer' : undefined,
                    background: filtroDep && filtroDep === l.departamento ? '#e3f2fd'
                      : l.situacao === 'pendente' ? '#fff8e6' : undefined }}>
                  <td>{l.departamento ? (<>
                    {l.departamento}
                    <i className={`fas ${filtroDep === l.departamento ? 'fa-filter-circle-xmark' : 'fa-magnifying-glass'} ms-2`}
                      style={{ color: '#008fa1', fontSize: '.75em' }} aria-hidden="true"></i>
                  </>) : <span className="text-danger">sem departamento</span>}
                    {(l.pendencias || []).length > 0 && <div className="small" style={{ color: '#a11d2b' }}>{l.pendencias.join(', ')}</div>}</td>
                  <td className="text-end tabular">{l.pessoas}</td>
                  <td className="text-end tabular">{brl(l.fgts)}</td>
                  <td className="text-end tabular">{brl(l.rescisorio)}</td>
                  <td className="text-end tabular">{brl(l.consignado || 0)}</td>
                  <td className="text-end tabular fw-bold">{brl(l.valor)}</td>
                  <td className="tabular">{l.vencimento || '—'}</td>
                </tr>))}</tbody>
              {(lote.linhas || []).length > 0 && (
                <tfoot><tr className="fw-bold">
                  <td>Total</td>
                  <td className="text-end tabular">{somaFgts('pessoas')}</td>
                  <td className="text-end tabular">{brl(somaFgts('fgts'))}</td>
                  <td className="text-end tabular">{brl(somaFgts('rescisorio'))}</td>
                  <td className="text-end tabular">{brl(somaFgts('consignado'))}</td>
                  <td className="text-end tabular">{brl(somaFgts('valor'))}</td>
                  <td></td>
                </tr></tfoot>)}
            </table></div></div></section>

          <section className="po-card po-enter mb-3"><header className="po-card-head">
            <h3 ref={colabFgtsRef}><i className="fas fa-users" aria-hidden="true"></i>Colaboradores{filtroDep ? ` · ${filtroDep}` : ''}</h3>
            <select className="form-select form-select-sm" style={{ width: 'auto', maxWidth: 260 }} value={filtroDep}
              onChange={(e) => setFiltroDep(e.target.value)} aria-label="Filtrar por departamento">
              <option value="">Todos os departamentos</option>
              {(lote.linhas || []).map((l) => l.departamento && (
                <option key={l.departamento} value={l.departamento}>{l.departamento} ({l.pessoas})</option>))}
            </select>
            <label className="d-flex align-items-center gap-2 small mb-0">
              <input type="checkbox" className="form-check-input m-0" checked={soPendentes} onChange={(e) => setSoPendentes(e.target.checked)} />Só pendentes</label></header>
            <div className="po-card-body"><div className="po-scroll"><table className="table table-hover po-table align-middle mb-0" style={{ fontSize: '.86rem' }}>
              <thead><tr><th style={{ minWidth: 200 }}>Nome</th><th>CPF</th><th>Filial</th><th>Seção</th><th className="text-end">FGTS</th><th className="text-end">Rescisório</th><th className="text-end">Consignado</th><th className="text-end">Total</th><th style={{ minWidth: 200 }}>Departamento</th><th>Origem</th><th>Situação</th></tr></thead>
              <tbody>{pessoasFgts.map((p, i) => {
                const semFgts = p.no_rateio === false;
                const salvando = ocupado === `fgts-${p.nome}`;
                const opcDepto = p.departamento && !departamentos.includes(p.departamento) ? [p.departamento, ...departamentos] : departamentos;
                return (
                  <tr key={`${p.nome}-${p.cnpj}-${p.secao}-${i}`} style={{ background: !semFgts && p.situacao === 'pendente' ? '#fff8e6' : undefined, opacity: semFgts ? 0.55 : undefined }}>
                    <td><div className="po-nome">{p.nome}</div>
                      {p.homonimo && <span className="badge bg-light text-dark border" title="Há mais de uma pessoa com este nome no arquivo: confira CPF e filial">nome repetido</span>}</td>
                    <td className="tabular">{mascara(p.cpf)}</td>
                    <td>{nomeFilial(p.cnpj)}</td>
                    <td>{p.secao || '—'}</td>
                    <td className="text-end tabular">{brl(p.fgts)}</td>
                    <td className="text-end tabular">{brl(p.resc)}</td>
                    <td className="text-end tabular">{brl(p.consignado || 0)}</td>
                    <td className="text-end tabular fw-bold">{brl(p.valor ?? p.total)}</td>
                    <td>{semFgts ? <span className="po-sub">sem FGTS</span> : (<>
                      <select className={`form-select form-select-sm ${!p.departamento ? 'border-warning' : ''}`} value={p.departamento || ''} disabled={salvando || !!ocupado || !departamentos.length}
                        onChange={(e) => {
                          const v = e.target.value;
                          salvarPessoaFgts(p, v === LIMPAR ? '' : v === FIXAR ? p.departamento : v);
                        }}>
                        <option value="" disabled>— escolher —</option>
                        {opcDepto.map((d) => <option key={d} value={d}>{d}</option>)}
                        {p.departamento_origem === 'convenia' && p.departamento && <option value={FIXAR}>— fixar "{p.departamento}" (salvar) —</option>}
                        {(p.departamento_origem === 'salvo' || p.departamento_origem === 'lote') && <option value={LIMPAR}>— limpar —</option>}
                      </select>
                      {p.departamento_convenia && <div className="po-sub">Convenia: {p.departamento_convenia}</div>}</>)}</td>
                    <td>{ORIGEM_FGTS[p.departamento_origem] || '—'}</td>
                    <td>{semFgts ? <span className="po-sub">sem FGTS</span> : p.situacao === 'ok'
                      ? <span className="badge" style={{ background: '#e6f4ea', color: '#146c43' }}>ok</span>
                      : <span className="badge" style={{ background: '#fdeaec', color: '#a11d2b' }}>pendente</span>}</td>
                  </tr>);
              })}
              {!pessoasFgts.length && <tr><td colSpan={10} className="text-muted small text-center py-3">{filtroDep ? `Ninguém em ${filtroDep} com este filtro.` : soPendentes ? 'Nenhum colaborador pendente.' : 'Nenhum colaborador no arquivo.'}</td></tr>}
              </tbody></table></div></div></section>
        </>) : (
          <div className="text-muted small py-3 text-center">Nenhum Encargos da Empresa lido para {dados.mes}. Suba o relatório para montar o rateio do FGTS.</div>)}
        </>) : (<>
        <div className="alert alert-light border small py-2"><i className="fas fa-circle-info me-2 text-primary" aria-hidden="true"></i>
          Suba o Relatório de Líquidos (.xls do Domínio) da competência. O Saber cruza cada pessoa pelo CPF com a Convenia e monta as linhas de contas a pagar da Omie.
          <div>Vencimento calculado: <b>{dados.vencimento || '—'}</b> (5º dia útil do mês seguinte). Contribuintes (pró-labore) ficam fora da planilha.</div></div>

        <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
          <input ref={relatorioRef} type="file" hidden accept=".xls" onChange={(e) => enviarArquivo(e.target.files[0])} />
          <button type="button" className="gr-btn primaria" disabled={!!ocupado} onClick={() => relatorioRef.current?.click()}>
            <i className={`fas ${ocupado === 'relatorio' ? 'fa-spinner fa-spin' : 'fa-file-arrow-up'}`} aria-hidden="true"></i>{ocupado === 'relatorio' ? 'Lendo o relatório…' : 'Subir Relatório de Líquidos (.xls)'}</button>
          {ehAdm && (<>
            <input ref={modeloRef} type="file" hidden accept=".xlsx" onChange={(e) => enviarArquivo(e.target.files[0], 'modelo')} />
            <button type="button" className="gr-btn" disabled={!!ocupado} onClick={() => modeloRef.current?.click()} title="Importa os departamentos por colaborador da planilha da Omie que já era usada">
              <i className={`fas ${ocupado === 'modelo' ? 'fa-spinner fa-spin' : 'fa-table-list'}`} aria-hidden="true"></i>Carregar de-para do modelo</button></>)}
          {dados.pessoas_salvas != null && <span className="po-sub">{dados.pessoas_salvas} colaborador(es) com projeto/departamento salvo</span>}
          <label className="ms-auto d-flex align-items-center gap-2 small mb-0" title="O que vai na coluna Fornecedor da planilha da Omie">
            Fornecedor:
            <select className="form-select form-select-sm" style={{ width: 'auto' }} value={fornecedor || dados?.config?.fornecedor || 'cpf'} onChange={(e) => setFornecedor(e.target.value)} disabled={!!ocupado}>
              <option value="cpf">CPF (só números)</option>
              <option value="nome">Nome do colaborador</option>
            </select>
          </label>
          <button type="button" className="gr-btn primaria" disabled={!podeExportar || !!ocupado} title={tituloExportar} onClick={() => exportar(lote.id)}>
            <i className={`fas ${lote && ocupado === `exportar-${lote.id}` ? 'fa-spinner fa-spin' : 'fa-file-excel'}`} aria-hidden="true"></i>Exportar planilha</button>
        </div>

        {semCpf && (
          <div className="alert alert-warning small py-2 d-flex">
            <div><i className="fas fa-triangle-exclamation me-2" aria-hidden="true"></i><b>Sem CPF no modelo ({semCpf.length}):</b> {semCpf.join(', ')}. Para estes, escolha o departamento na tabela quando aparecerem no relatório.</div>
            <button type="button" className="btn-close ms-auto" aria-label="Fechar" onClick={() => setSemCpf(null)}></button>
          </div>)}

        {lote && !departamentos.length && (
          <div className="alert alert-warning small py-2"><i className="fas fa-triangle-exclamation me-2" aria-hidden="true"></i>
            Ainda não há lista de departamentos da Omie — peça ao administrador para carregar o de-para do modelo ou configurar os departamentos.</div>)}

        {lote ? (<>
          <div className="row g-3 mb-3">
            {[
              ['Pessoas', lote.qtd, 'fa-users', undefined],
              ['Total', brl(lote.total), 'fa-sack-dollar', undefined],
              ['Pendências', lote.pendentes, 'fa-triangle-exclamation', lote.pendentes > 0 ? '#a11d2b' : undefined],
              ['Fora da planilha', (lote.fora || []).length, 'fa-user-slash', undefined],
              ['Novos no mês', lote.novos || 0, 'fa-user-plus', (lote.novos || 0) > 0 ? '#0a6ea1' : undefined],
            ].map(([rot, val, ic, cor]) => (
              <div className="col-6 col-lg" key={rot}>
                <section className="po-card po-enter h-100"><div className="po-card-body">
                  <div className="po-sub"><i className={`fas ${ic} me-1`} aria-hidden="true"></i>{rot}</div>
                  <div className="fs-4 fw-bold tabular" style={{ color: cor }}>{val}</div>
                </div></section></div>))}
          </div>

          {(lote.avisos || []).length > 0 && (
            <div className="alert alert-warning small py-2">{lote.avisos.map((a, i) => <div key={`${i}-${a}`}><i className="fas fa-triangle-exclamation me-1" aria-hidden="true"></i>{a}</div>)}</div>)}

          <section className="po-card po-enter mb-3"><header className="po-card-head">
            <h3><i className="fas fa-table" aria-hidden="true"></i>{lote.arquivo || 'Relatório'} · {dados.mes}</h3>
            <span className="po-sub">{lote.gerado_por} · {lote.gerado_em}</span></header>
            <div className="po-card-body"><div className="po-scroll"><table className="table table-hover po-table align-middle mb-0" style={{ fontSize: '.86rem' }}>
              <thead><tr><th style={{ minWidth: 200 }}>Colaborador</th><th>CPF</th><th>Categoria</th><th style={{ minWidth: 170 }}>Projeto</th><th style={{ minWidth: 200 }}>Departamento</th><th className="text-end">Valor</th><th>Vencimento</th><th>Situação</th></tr></thead>
              <tbody>{(lote.linhas || []).map((l, i) => {
                // de propósito por CPF: a escolha de projeto/departamento vale para a
                // pessoa, então as linhas do mesmo CPF (dois CNPJs) travam juntas
                const salvando = ocupado === `pessoa-${l.cpf}`;
                const opcDepto = l.departamento && !departamentos.includes(l.departamento) ? [l.departamento, ...departamentos] : departamentos;
                return (
                  <tr key={`${l.cpf}-${l.cnpj}-${l.secao}-${i}`} style={{ background: l.situacao === 'pendente' ? '#fff8e6' : undefined }}>
                    <td><div className="po-nome">{l.nome}</div>
                      {l.nome_convenia && l.nome_convenia !== l.nome && <div className="po-sub">Convenia: {l.nome_convenia}</div>}
                      {l.alerta && <div className="small" style={{ color: '#a11d2b' }}><i className="fas fa-triangle-exclamation me-1" aria-hidden="true"></i>{l.alerta}</div>}</td>
                    <td className="tabular">{mascara(l.cpf)}</td>
                    <td>{l.categoria || '—'}</td>
                    <td>{l.projeto ? l.projeto : (
                      <select className="form-select form-select-sm border-warning" value="" disabled={salvando} onChange={(e) => e.target.value && salvarPessoa(l, 'projeto', e.target.value)}>
                        <option value="">Escolha…</option>
                        {projetos.map((p) => <option key={p} value={p}>{p}</option>)}
                      </select>)}
                      <div className="po-sub">Convenia: {l.filial_convenia || '—'}</div></td>
                    <td><select className={`form-select form-select-sm ${!l.departamento ? 'border-warning' : ''}`} value={l.departamento || ''} disabled={salvando || !departamentos.length}
                      onChange={(e) => salvarPessoa(l, 'departamento', e.target.value === LIMPAR ? '' : e.target.value)}>
                      <option value="" disabled>Escolha…</option>
                      {opcDepto.map((d) => <option key={d} value={d}>{d}</option>)}
                      {l.departamento_origem === 'salvo' && <option value={LIMPAR}>— limpar (voltar à Convenia) —</option>}
                    </select>
                      <div className="po-sub">Convenia: {l.departamento_convenia || '—'}{l.departamento_origem === 'salvo' ? ' · salvo' : ''}</div></td>
                    <td className="text-end tabular fw-bold">{brl(l.valor)}</td>
                    <td className="tabular">{l.vencimento || '—'}</td>
                    <td>{l.situacao === 'ok'
                      ? <span className="badge" style={{ background: '#e6f4ea', color: '#146c43' }}>ok</span>
                      : <span className="badge" style={{ background: '#fdeaec', color: '#a11d2b' }}>pendente{(l.pendencias || []).length ? `: ${l.pendencias.join(', ')}` : ''}</span>}</td>
                  </tr>);
              })}</tbody></table></div>
              {(lote.fora || []).length > 0 && (
                <div className="small text-muted mt-2"><b>Fora da planilha:</b>{' '}
                  {lote.fora.map((f, i) => <span key={`${f.cpf}-${i}`}>{i ? '; ' : ''}{f.nome} ({mascara(f.cpf)}, {brl(f.valor)}) — {f.motivo}</span>)}</div>)}
            </div></section>

          {/* Novos colaboradores: admitidos na competência (Convenia) ou que aparecem pela
              primeira vez no relatório. A operadora confere projeto e departamento antes de
              exportar — é onde o erro de de-para costuma entrar. */}
          {(lote.linhas || []).some((l) => l.novo) && (
            <section className="po-card po-enter mb-3"><header className="po-card-head">
              <h3><i className="fas fa-user-plus" aria-hidden="true"></i>Novos colaboradores neste mês</h3>
              <span className="badge bg-light text-dark">{lote.linhas.filter((l) => l.novo).length}</span></header>
              <div className="po-card-body">
                <div className="alert alert-light border small py-2 mb-2"><i className="fas fa-circle-info me-2 text-primary" aria-hidden="true"></i>
                  Confira o <b>projeto</b> e o <b>departamento</b> de cada um: o projeto vem do CNPJ em que a folha foi apurada e o departamento do cadastro da Convenia traduzido para o nome da Omie (ou do que você escolher na tabela acima).</div>
                <div className="po-scroll"><table className="table table-hover po-table align-middle mb-0">
                  <thead><tr><th>Colaborador</th><th>CPF</th><th>Admissão (Convenia)</th><th>Por quê</th><th>Categoria</th><th>Projeto</th><th>Departamento</th><th className="text-end">Valor</th><th>Situação</th></tr></thead>
                  <tbody>{lote.linhas.filter((l) => l.novo).map((l, i) => (
                    <tr key={`novo-${l.cpf}-${l.cnpj}-${i}`} style={l.situacao === 'pendente' ? { background: '#fff8e6' } : undefined}>
                      <td><b>{l.nome}</b>{l.nome_convenia && l.nome_convenia.toUpperCase() !== (l.nome || '').toUpperCase() && <div className="po-sub">Convenia: {l.nome_convenia}</div>}</td>
                      <td className="tabular">{mascara(l.cpf)}</td>
                      <td className="tabular">{l.admissao_convenia || '—'}</td>
                      <td className="small">{l.novo_motivo}</td>
                      <td>{l.categoria}</td>
                      <td>{l.projeto || <span className="text-danger">sem projeto</span>}{l.filial_convenia && <div className="po-sub">Convenia: {l.filial_convenia}</div>}</td>
                      <td>{l.departamento || <span className="text-danger">sem departamento</span>}
                        <div className="po-sub">Convenia: {l.departamento_convenia || '—'}{l.departamento_origem === 'salvo' ? ' · salvo' : l.departamento_origem === 'convenia' ? ' · traduzido' : ''}</div></td>
                      <td className="text-end tabular fw-bold">{brl(l.valor)}</td>
                      <td>{l.situacao === 'ok'
                        ? <span className="badge" style={{ background: '#e6f4ea', color: '#146c43' }}>ok</span>
                        : <span className="badge" style={{ background: '#fdeaec', color: '#a11d2b' }}>pendente</span>}</td>
                    </tr>))}</tbody></table></div>
              </div></section>)}
        </>) : (
          <div className="text-muted small py-3 text-center">Nenhum relatório lido para {dados.mes}. Suba o Relatório de Líquidos para montar a planilha.</div>)}

        </>)}

        {(dados.historico || []).length > 0 && (
          <section className="po-card po-enter"><header className="po-card-head"><h3><i className="fas fa-clock-rotate-left" aria-hidden="true"></i>Relatórios lidos</h3></header>
            <div className="po-card-body"><div className="po-scroll"><table className="table table-hover po-table align-middle mb-0">
              <thead><tr><th>Competência</th><th className="text-end">Pessoas</th><th className="text-end">Total</th><th>Lido por</th><th>Em</th><th></th></tr></thead>
              <tbody>{dados.historico.map((h) => (
                <tr key={h.id}>
                  <td>{h.mes}</td><td className="text-end tabular">{h.qtd}</td><td className="text-end tabular">{brl(h.total)}</td>
                  <td>{h.gerado_por || '—'}</td><td className="po-sub">{h.gerado_em}</td>
                  <td className="text-end"><button type="button" className="gr-btn" disabled={!!ocupado || h.pendentes > 0} onClick={() => exportar(h.id)}
                    title={h.pendentes > 0 ? `${h.pendentes} pendência(s) — resolva antes de exportar` : 'Baixar a planilha da Omie deste relatório'}>
                    <i className={`fas ${ocupado === `exportar-${h.id}` ? 'fa-spinner fa-spin' : 'fa-download'}`} aria-hidden="true"></i>Baixar</button></td>
                </tr>))}</tbody></table></div></div></section>)}
      </>)}

      {cfg && (
        <div className="modal" style={{ display: 'block', background: 'rgba(0,0,0,.5)' }} tabIndex="-1" role="dialog" aria-modal="true">
          <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable"><div className="modal-content">
            <div className="modal-header" style={{ background: '#fff' }}>
              <h5 className="modal-title" style={{ color: '#212529' }}><i className="fas fa-gear me-2" aria-hidden="true"></i>Configuração da Omie<div className="small fw-normal" style={{ color: '#6c757d' }}>{dados?.cliente}</div></h5>
              <button type="button" className="btn-close" aria-label="Fechar" disabled={ocupado === 'config'} onClick={() => setCfg(null)}></button></div>
            <div className="modal-body">
              <label className="form-label small fw-bold mb-1">Projeto por CNPJ (cnpj=Projeto, um por linha)</label>
              <textarea className="form-control form-control-sm mb-3" rows={4} value={cfg.projetos} onChange={(e) => setCfg({ ...cfg, projetos: e.target.value })} />
              <label className="form-label small fw-bold mb-1">Departamentos válidos na Omie (um por linha)</label>
              <textarea className="form-control form-control-sm mb-3" rows={6} value={cfg.departamentos} onChange={(e) => setCfg({ ...cfg, departamentos: e.target.value })} />
              <label className="form-label small fw-bold mb-1">De-para Convenia → Omie (convenia=Omie, um por linha)</label>
              <textarea className="form-control form-control-sm mb-3" rows={5} value={cfg.depara} onChange={(e) => setCfg({ ...cfg, depara: e.target.value })} />
              <div className="row g-2">
                <div className="col-md-4"><label className="form-label small fw-bold mb-1">Conta corrente</label>
                  <input type="text" className="form-control form-control-sm" value={cfg.conta_corrente} onChange={(e) => setCfg({ ...cfg, conta_corrente: e.target.value })} /></div>
                <div className="col-md-4"><label className="form-label small fw-bold mb-1">Categoria CLT</label>
                  <input type="text" className="form-control form-control-sm" value={cfg.categoria_clt} onChange={(e) => setCfg({ ...cfg, categoria_clt: e.target.value })} /></div>
                <div className="col-md-4"><label className="form-label small fw-bold mb-1">Categoria estágio</label>
                  <input type="text" className="form-control form-control-sm" value={cfg.categoria_estagio} onChange={(e) => setCfg({ ...cfg, categoria_estagio: e.target.value })} /></div>
                <div className="col-md-4"><label className="form-label small fw-bold mb-1">Fornecedor (padrão ao exportar)</label>
                  <select className="form-select form-select-sm" value={cfg.fornecedor || 'cpf'} onChange={(e) => setCfg({ ...cfg, fornecedor: e.target.value })}>
                    <option value="cpf">CPF (só números)</option>
                    <option value="nome">Nome do colaborador</option>
                  </select></div>
              </div>
              <h6 className="fw-bold mt-4 mb-2"><i className="fas fa-sitemap me-2" aria-hidden="true"></i>Rateio do FGTS</h6>
              <div className="row g-2">
                <div className="col-md-4"><label className="form-label small fw-bold mb-1">Fornecedor</label>
                  <input type="text" className="form-control form-control-sm" placeholder="RECEITA FEDERAL - FGTS" value={cfg.fgts_fornecedor} onChange={(e) => setCfg({ ...cfg, fgts_fornecedor: e.target.value })} /></div>
                <div className="col-md-4"><label className="form-label small fw-bold mb-1">Categoria</label>
                  <input type="text" className="form-control form-control-sm" placeholder="FGTS" value={cfg.fgts_categoria} onChange={(e) => setCfg({ ...cfg, fgts_categoria: e.target.value })} /></div>
                <div className="col-md-4"><label className="form-label small fw-bold mb-1">Conta corrente</label>
                  <input type="text" className="form-control form-control-sm" placeholder="DARF - IMPOSTOS FEDERAIS" value={cfg.fgts_conta} onChange={(e) => setCfg({ ...cfg, fgts_conta: e.target.value })} /></div>
              </div>
            </div>
            <div className="modal-footer bg-light">
              <button type="button" className="btn btn-secondary" disabled={ocupado === 'config'} onClick={() => setCfg(null)}>Cancelar</button>
              <button type="button" className="btn btn-primary" disabled={ocupado === 'config'} onClick={salvarCfg}>
                <i className={`fas ${ocupado === 'config' ? 'fa-spinner fa-spin' : 'fa-floppy-disk'} me-2`} aria-hidden="true"></i>Salvar</button>
            </div>
          </div></div></div>)}
    </div></div>
  );
};

export default BpoRhImportacaoOmie;
